import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

const DISMISS_KEY = 'pwa_install_dismissed_at';
const DISMISS_TTL_MS = 1000 * 60 * 60 * 24 * 14; // 14 days

const isStandalone = () => {
  if (typeof window === 'undefined') return false;
  const mq = window.matchMedia && window.matchMedia('(display-mode: standalone)').matches;
  return Boolean(mq || window.navigator.standalone);
};

const isIos = () => {
  if (typeof navigator === 'undefined') return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
};

const recentlyDismissed = () => {
  const raw = localStorage.getItem(DISMISS_KEY);
  if (!raw) return false;
  const ts = Number(raw);
  if (!ts) return false;
  return Date.now() - ts < DISMISS_TTL_MS;
};

export const PWAInstallPrompt = () => {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [iosHint, setIosHint] = useState(false);
  const [iosModalOpen, setIosModalOpen] = useState(false);

  useEffect(() => {
    if (isStandalone()) return undefined;
    if (recentlyDismissed()) return undefined;

    const handler = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };

    window.addEventListener('beforeinstallprompt', handler);

    if (isIos()) {
      setIosHint(true);
      setVisible(true);
    }

    const installedHandler = () => {
      setVisible(false);
      setDeferred(null);
    };
    window.addEventListener('appinstalled', installedHandler);

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      window.removeEventListener('appinstalled', installedHandler);
    };
  }, []);

  const handleDismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setVisible(false);
  };

  // Android / Chrome / Edge: programmatic install. One click, OS-level prompt.
  const handleInstall = async () => {
    if (!deferred) return;
    deferred.prompt();
    const choice = await deferred.userChoice;
    if (choice.outcome !== 'accepted') {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    }
    setDeferred(null);
    setVisible(false);
  };

  // iOS Safari: Apple disallows programmatic install. Best we can do is
  // open a small modal that walks them through the two taps with a clear
  // visual. Replaces the long instructional banner copy.
  const handleIosInstall = () => setIosModalOpen(true);

  if (!visible) return null;

  return (
    <>
      <Banner role="dialog" aria-label="Install SmrtStats">
        <Text>
          {iosHint
            ? 'Add SmrtStats to your home screen for a faster, app-like experience.'
            : 'Install SmrtStats for a faster, full-screen experience.'}
        </Text>
        <Actions>
          {!iosHint && deferred && (
            <InstallButton type="button" onClick={handleInstall}>
              Install
            </InstallButton>
          )}
          {iosHint && (
            <InstallButton type="button" onClick={handleIosInstall}>
              Install
            </InstallButton>
          )}
          <DismissButton type="button" onClick={handleDismiss} aria-label="Dismiss install prompt">
            Not now
          </DismissButton>
        </Actions>
      </Banner>

      {iosModalOpen && (
        <ModalOverlay onClick={() => setIosModalOpen(false)} role="dialog" aria-modal="true">
          <ModalCard onClick={(e) => e.stopPropagation()}>
            <ModalTitle>Install SmrtStats</ModalTitle>
            <ModalSubtitle>
              iOS Safari doesn't allow apps to install themselves — but it's two taps:
            </ModalSubtitle>

            <Step>
              <StepNumber>1</StepNumber>
              <StepText>
                Tap the <ShareIconInline aria-hidden>
                  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 3v12" />
                    <path d="m7 8 5-5 5 5" />
                    <rect x="4" y="13" width="16" height="8" rx="2" />
                  </svg>
                </ShareIconInline> Share button at the bottom of Safari
              </StepText>
            </Step>

            <Step>
              <StepNumber>2</StepNumber>
              <StepText>
                Scroll the share sheet and tap <strong>Add to Home Screen</strong>
              </StepText>
            </Step>

            <ModalActions>
              <DismissButton type="button" onClick={() => setIosModalOpen(false)}>
                Got it
              </DismissButton>
            </ModalActions>
          </ModalCard>
        </ModalOverlay>
      )}
    </>
  );
};

export default PWAInstallPrompt;

const Banner = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 14px;
  margin-bottom: 12px;
  border-radius: 10px;
  background: ${(props) => props.theme.row};
  border: 1px solid ${(props) => props.theme.lines};
  font-size: 13px;
  font-family: 'SatoshiMedium', sans-serif;

  /* Narrow viewports — let actions wrap below the text instead of
     pushing them past the right edge of the screen. */
  flex-wrap: wrap;
`;

const Text = styled.span`
  flex: 1 1 200px;
  min-width: 0;
  line-height: 1.4;
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  margin-left: auto;
`;

const InstallButton = styled.button`
  padding: 6px 12px;
  border-radius: 8px;
  border: none;
  background: ${(props) => props.theme.green};
  color: ${(props) => props.theme.black};
  font-family: 'SatoshiBold', sans-serif;
  font-size: 13px;
  cursor: pointer;
  &:hover { filter: brightness(0.95); }
`;

const DismissButton = styled.button`
  padding: 6px 10px;
  border-radius: 8px;
  background: transparent;
  border: 1px solid ${(props) => props.theme.lines};
  color: ${(props) => props.theme.blueish};
  font-family: 'SatoshiMedium', sans-serif;
  font-size: 13px;
  cursor: pointer;
  &:hover { color: ${(props) => props.theme.white}; }
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  z-index: 50;
  backdrop-filter: blur(2px);
`;

const ModalCard = styled.div`
  width: 100%;
  max-width: 360px;
  background: ${(props) => props.theme.cardBg ?? '#11141A'};
  border-radius: 14px;
  padding: 24px 22px 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  color: ${(props) => props.theme.white};
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-family: 'SatoshiBold', sans-serif;
  font-size: 20px;
`;

const ModalSubtitle = styled.p`
  margin: 0 0 6px;
  font-size: 13px;
  line-height: 1.45;
  color: ${(props) => props.theme.blueish2};
`;

const Step = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
`;

const StepNumber = styled.span`
  flex-shrink: 0;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: ${(props) => props.theme.green};
  color: ${(props) => props.theme.black};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-family: 'SatoshiBold', sans-serif;
  font-size: 13px;
`;

const StepText = styled.span`
  font-size: 14px;
  line-height: 1.5;
  color: ${(props) => props.theme.white};
  strong { color: ${(props) => props.theme.green}; }
`;

const ShareIconInline = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 6px;
  background: rgba(200, 255, 0, 0.12);
  color: ${(props) => props.theme.green};
  margin: 0 2px;
  vertical-align: -7px;
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 8px;
`;
